import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Upload,
  FileSpreadsheet,
  Download,
  AlertTriangle,
  CheckCircle2,
  Trash2,
  UserCheck,
  Loader2,
  ArrowLeft,
} from "lucide-react";

/**
 * Wizard de importación de clientes (mar/2026).
 *
 * Paso 1: sube CSV/XLSX (con opción de descargar plantilla).
 * Paso 2: preview editable —
 *   - Cada fila muestra los campos como inputs (editables).
 *   - Si el sistema detectó un duplicado, muestra una alerta con el nombre existente
 *     y el usuario decide: SALTAR / ACTUALIZAR / CREAR IGUAL.
 *   - Se puede EXCLUIR una fila con el botón basurita.
 *   - Botón "Importar" ejecuta /clientes/importar/confirmar con las filas restantes.
 * Paso 3: resumen con creados / actualizados / saltados / errores.
 */
export default function ImportClientesWizard({ open, onOpenChange, onSuccess }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null); // {total, nuevos, duplicados, rows, ...}
  const [rows, setRows] = useState([]);
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setStep(1);
    setFile(null);
    setLoading(false);
    setPreview(null);
    setRows([]);
    setResult(null);
    setSaving(false);
  };

  const closeAll = (v) => {
    if (loading || saving) return; // no cerrar durante operaciones
    if (!v) reset();
    onOpenChange(v);
  };

  const downloadPlantilla = async () => {
    try {
      const { data } = await api.get("/clientes/_/plantilla-import", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "plantilla_clientes_beem.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("No se pudo descargar la plantilla");
    }
  };

  const uploadAndPreview = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/clientes/importar/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      // Cada fila lleva su propia flag `_incluir` para poder excluir sin perderla del array
      setRows((data.rows || []).map((r) => ({ ...r, _incluir: true })));
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo analizar el archivo");
    } finally {
      setLoading(false);
    }
  };

  const updateRow = (idx, patch) => {
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const removeRow = (idx) => updateRow(idx, { _incluir: false });
  const restoreRow = (idx) => updateRow(idx, { _incluir: true });

  const contadores = useMemo(() => {
    const activos = rows.filter((r) => r._incluir);
    return {
      total: activos.length,
      crear: activos.filter((r) => r.accion === "crear").length,
      actualizar: activos.filter((r) => r.accion === "actualizar").length,
      saltar: activos.filter((r) => r.accion === "saltar").length,
      excluidos: rows.length - activos.length,
    };
  }, [rows]);

  const confirmar = async () => {
    const activos = rows.filter((r) => r._incluir).map((r) => {
      const { _incluir, duplicate_of, ...clean } = r;
      return clean;
    });
    if (activos.length === 0) {
      toast.error("No hay filas para importar");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/clientes/importar/confirmar", { rows: activos });
      setResult(data);
      setStep(3);
      if ((data.creados || 0) + (data.actualizados || 0) > 0) {
        onSuccess?.();
        toast.success(`${data.creados} creado(s), ${data.actualizados} actualizado(s)`);
      } else if ((data.errores || []).length > 0) {
        toast.error("Importación completada con errores");
      } else {
        toast.info(`${data.saltados} fila(s) saltada(s)`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo importar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={closeAll}>
      <DialogContent
        className="max-w-6xl max-h-[92vh] flex flex-col"
        data-testid="import-clientes-wizard"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <FileSpreadsheet className="w-6 h-6 text-primary" /> Importar clientes
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Sube un CSV o Excel con tu lista de clientes. Revisa el preview, edita lo que necesites y confirma.
          </p>
        </DialogHeader>

        {/* PASO 1: subir archivo */}
        {step === 1 && (
          <div className="mt-4 space-y-4">
            <div className="p-4 bg-muted/40 border border-border rounded-lg flex items-center justify-between gap-3">
              <div className="flex-1">
                <div className="text-sm font-semibold text-foreground">Plantilla oficial</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Solo <b>nombre</b> es obligatorio. Reconoce columnas equivalentes (celular, correo, cédula, NIT, etc).
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={downloadPlantilla}
                data-testid="download-plantilla-wizard"
              >
                <Download className="w-4 h-4 mr-2" /> Descargar plantilla
              </Button>
            </div>

            <div>
              <Label>Archivo (.csv, .xlsx)</Label>
              <label className="mt-1 flex items-center gap-3 h-16 px-4 border-2 border-dashed border-border rounded-lg hover:border-primary/50 cursor-pointer bg-card transition-colors">
                <Upload className="w-5 h-5 text-muted-foreground/70" />
                <div className="flex-1 text-sm text-muted-foreground">
                  {file ? (
                    <span className="font-medium text-foreground">{file.name}</span>
                  ) : (
                    "Seleccionar archivo…"
                  )}
                </div>
                <input
                  type="file"
                  accept=".csv,.xlsx,.xlsm"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="hidden"
                  data-testid="import-cli-wizard-file"
                />
              </label>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => closeAll(false)} disabled={loading}>
                Cancelar
              </Button>
              <Button
                disabled={!file || loading}
                onClick={uploadAndPreview}
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
                data-testid="import-cli-analyze-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analizando…
                  </>
                ) : (
                  <>Analizar y previsualizar</>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* PASO 2: preview editable */}
        {step === 2 && preview && (
          <div className="flex-1 flex flex-col overflow-hidden mt-3">
            {/* Resumen */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
              <StatBox label="Filas archivo" value={preview.total} tone="slate" />
              <StatBox label="Nuevos" value={preview.nuevos} tone="emerald" />
              <StatBox label="Duplicados" value={preview.duplicados} tone="amber" />
              <StatBox
                label="A crear/actualizar"
                value={contadores.crear + contadores.actualizar}
                tone="primary"
              />
              <StatBox
                label="Excluidos"
                value={contadores.excluidos + contadores.saltar}
                tone="muted"
              />
            </div>

            {(preview.columnas_faltantes || []).length > 0 && (
              <div className="mb-2 p-2 border border-amber-500/30 bg-amber-500/5 rounded-md text-xs text-amber-800 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <div>
                  Columnas no encontradas en el archivo: <b>{preview.columnas_faltantes.join(", ")}</b>.
                  Puedes completar esos campos manualmente por fila.
                </div>
              </div>
            )}

            {/* Tabla scrollable */}
            <div className="flex-1 overflow-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted/70 backdrop-blur text-[11px] uppercase tracking-wider text-muted-foreground z-10">
                  <tr>
                    <th className="p-2 text-left w-8"></th>
                    <th className="p-2 text-left">Nombre*</th>
                    <th className="p-2 text-left">Nombre comercial</th>
                    <th className="p-2 text-left">Doc</th>
                    <th className="p-2 text-left">Teléfono</th>
                    <th className="p-2 text-left">Tel. alterno</th>
                    <th className="p-2 text-left">Email</th>
                    <th className="p-2 text-left">Ciudad</th>
                    <th className="p-2 text-left">Depto</th>
                    <th className="p-2 text-left">Dirección</th>
                    <th className="p-2 text-left">Segmento</th>
                    <th className="p-2 text-left">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => (
                    <RowEditor
                      key={r.fila || idx}
                      idx={idx}
                      row={r}
                      onChange={(patch) => updateRow(idx, patch)}
                      onRemove={() => removeRow(idx)}
                      onRestore={() => restoreRow(idx)}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between items-center gap-2 pt-3 border-t border-border mt-3">
              <Button variant="outline" onClick={() => setStep(1)} disabled={saving}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Volver
              </Button>
              <div className="text-xs text-muted-foreground">
                Se importarán <b className="text-foreground">{contadores.crear + contadores.actualizar}</b> filas
                ({contadores.crear} nuevas · {contadores.actualizar} actualizaciones ·{" "}
                <span className="text-muted-foreground">{contadores.saltar + contadores.excluidos} omitidas</span>)
              </div>
              <Button
                onClick={confirmar}
                disabled={saving || contadores.crear + contadores.actualizar === 0}
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
                data-testid="import-cli-confirm-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importando…
                  </>
                ) : (
                  <>
                    <UserCheck className="w-4 h-4 mr-2" /> Importar {contadores.crear + contadores.actualizar}
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* PASO 3: resumen */}
        {step === 3 && result && (
          <div className="mt-4 space-y-4">
            <div className="p-6 bg-card border border-border rounded-xl">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                <div className="font-display text-xl">Importación completada</div>
              </div>
              <div className="grid grid-cols-4 gap-3 text-center">
                <ResumenBlock label="Creados" value={result.creados} tone="emerald" />
                <ResumenBlock label="Actualizados" value={result.actualizados} tone="blue" />
                <ResumenBlock label="Saltados" value={result.saltados} tone="slate" />
                <ResumenBlock
                  label="Errores"
                  value={(result.errores || []).length}
                  tone={(result.errores || []).length ? "rose" : "slate"}
                />
              </div>
            </div>

            {(result.errores || []).length > 0 && (
              <div className="max-h-64 overflow-auto rounded-lg border border-rose-300/40 bg-rose-50/40 p-3 text-sm">
                <div className="font-semibold text-rose-800 mb-2 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" /> Errores por fila
                </div>
                <ul className="space-y-1 text-xs text-rose-900">
                  {result.errores.map((e, i) => (
                    <li key={i}>
                      • Fila <b>{e.fila}</b>: {e.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={reset}>
                Importar otro archivo
              </Button>
              <Button
                onClick={() => closeAll(false)}
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                Cerrar
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------- Sub-componentes ----------

function StatBox({ label, value, tone = "slate" }) {
  const colors = {
    slate: "text-foreground bg-muted/50",
    emerald: "text-emerald-800 bg-emerald-50 border-emerald-200",
    amber: "text-amber-800 bg-amber-50 border-amber-200",
    primary: "text-primary bg-primary/5 border-primary/20",
    muted: "text-muted-foreground bg-muted/30",
  };
  return (
    <div className={`rounded-lg border border-border p-2.5 text-center ${colors[tone]}`}>
      <div className="text-xl font-semibold tabular-nums">{value ?? 0}</div>
      <div className="text-[10px] uppercase tracking-wider font-bold opacity-70 mt-0.5">
        {label}
      </div>
    </div>
  );
}

function ResumenBlock({ label, value, tone }) {
  const colors = {
    emerald: "text-emerald-600",
    blue: "text-blue-600",
    rose: "text-rose-600",
    slate: "text-muted-foreground",
  };
  return (
    <div>
      <div className={`text-3xl font-semibold tabular-nums ${colors[tone]}`}>{value}</div>
      <div className="text-[10px] uppercase text-muted-foreground font-bold mt-1">{label}</div>
    </div>
  );
}

function RowEditor({ idx, row, onChange, onRemove, onRestore }) {
  const excluido = !row._incluir;
  const dup = row.duplicate_of;
  const inputBase =
    "h-8 px-2 w-full min-w-[110px] rounded-md border border-border bg-background text-xs disabled:opacity-50";

  return (
    <>
      <tr
        className={`border-t border-border ${
          excluido ? "opacity-40 bg-muted/20" : dup ? "bg-amber-50/30" : "hover:bg-muted/20"
        }`}
        data-testid={`import-row-${idx}`}
      >
        <td className="p-2 align-top">
          {excluido ? (
            <button
              type="button"
              onClick={onRestore}
              className="text-primary hover:underline text-[10px] font-bold"
              title="Restaurar fila"
            >
              ⟲
            </button>
          ) : (
            <button
              type="button"
              onClick={onRemove}
              className="text-muted-foreground hover:text-rose-600 p-1"
              title="Excluir esta fila"
              data-testid={`import-row-${idx}-remove`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </td>
        <td className="p-1.5 align-top">
          <Input
            value={row.nombre || ""}
            onChange={(e) => onChange({ nombre: e.target.value })}
            className={inputBase}
            disabled={excluido}
            data-testid={`import-row-${idx}-nombre`}
          />
        </td>
        <td className="p-1.5 align-top">
          <Input
            value={row.nombre_comercial || ""}
            onChange={(e) => onChange({ nombre_comercial: e.target.value })}
            className={inputBase}
            disabled={excluido}
          />
        </td>
        <td className="p-1.5 align-top">
          <div className="flex gap-1 items-center">
            <select
              value={row.tipo_documento || ""}
              onChange={(e) => onChange({ tipo_documento: e.target.value || null })}
              className="h-8 px-1 text-xs rounded-md border border-border bg-background"
              disabled={excluido}
            >
              <option value="">—</option>
              <option value="cc">CC</option>
              <option value="nit">NIT</option>
            </select>
            <Input
              value={row.numero_documento || ""}
              onChange={(e) => onChange({ numero_documento: e.target.value })}
              className="h-8 px-2 w-24 min-w-[80px] rounded-md border border-border bg-background text-xs"
              disabled={excluido}
              placeholder="Número"
            />
          </div>
        </td>
        <td className="p-1.5 align-top">
          <Input
            value={row.telefono || ""}
            onChange={(e) => onChange({ telefono: e.target.value })}
            className={inputBase}
            disabled={excluido}
          />
        </td>
        <td className="p-1.5 align-top">
          <Input
            value={row.telefono_alterno || ""}
            onChange={(e) => onChange({ telefono_alterno: e.target.value })}
            className={inputBase}
            disabled={excluido}
          />
        </td>
        <td className="p-1.5 align-top">
          <Input
            value={row.email || ""}
            onChange={(e) => onChange({ email: e.target.value })}
            className={inputBase}
            disabled={excluido}
          />
        </td>
        <td className="p-1.5 align-top">
          <Input
            value={row.ciudad || ""}
            onChange={(e) => onChange({ ciudad: e.target.value })}
            className={inputBase}
            disabled={excluido}
          />
        </td>
        <td className="p-1.5 align-top">
          <Input
            value={row.departamento || ""}
            onChange={(e) => onChange({ departamento: e.target.value })}
            className={inputBase}
            disabled={excluido}
          />
        </td>
        <td className="p-1.5 align-top">
          <Input
            value={row.direccion || ""}
            onChange={(e) => onChange({ direccion: e.target.value })}
            className={inputBase}
            disabled={excluido}
          />
        </td>
        <td className="p-1.5 align-top">
          <select
            value={row.segmento || "minorista"}
            onChange={(e) => onChange({ segmento: e.target.value })}
            className="h-8 px-1 text-xs rounded-md border border-border bg-background"
            disabled={excluido}
          >
            <option value="minorista">Minorista</option>
            <option value="mayorista">Mayorista</option>
            <option value="vip">VIP</option>
          </select>
        </td>
        <td className="p-1.5 align-top">
          <select
            value={row.accion || "crear"}
            onChange={(e) => onChange({ accion: e.target.value })}
            className={`h-8 px-2 text-xs rounded-md border font-semibold ${
              row.accion === "crear"
                ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                : row.accion === "actualizar"
                ? "border-blue-300 bg-blue-50 text-blue-800"
                : "border-border bg-muted/40 text-muted-foreground"
            }`}
            disabled={excluido}
            data-testid={`import-row-${idx}-accion`}
          >
            {dup ? (
              <>
                <option value="saltar">Saltar</option>
                <option value="actualizar">Actualizar</option>
                <option value="crear">Crear igual</option>
              </>
            ) : (
              <>
                <option value="crear">Crear</option>
                <option value="saltar">Saltar</option>
              </>
            )}
          </select>
        </td>
      </tr>
      {dup && !excluido && (
        <tr className="bg-amber-50/40">
          <td colSpan={12} className="px-3 py-1.5 text-[11px] text-amber-900 border-t border-amber-200/50">
            <AlertTriangle className="w-3 h-3 inline mr-1" />
            <b>Ya existe un cliente</b>: {dup.nombre}
            {dup.documento ? ` · doc ${dup.documento}` : ""}
            {dup.telefono ? ` · tel ${dup.telefono}` : ""}
            {dup.vendedor_nombre ? ` · vendedor: ${dup.vendedor_nombre}` : ""}
          </td>
        </tr>
      )}
    </>
  );
}
